import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FIGUTOUR_STEPS } from "@/components/figutour/FIGUTOUR_STEPS";
import { useRoboBallExpression } from "@/components/figutour/RoboBallExpressionContext";
import FiguTourOverlay from "@/components/figutour/FiguTourOverlay";
import FiguTourBubble from "@/components/figutour/FiguTourBubble";
import { logEvent } from "@/lib/telemetry";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * FiguTour state machine (Phase 16 v2 rewrite).
 *
 * Single source of truth: { isActive, stepIndex }. Everything downstream —
 * bubble text, spotlight target, Robo-Ball position, button labels —
 * derives from `FIGUTOUR_STEPS[stepIndex]`. No parallel state, no dead
 * branches.
 */
const INITIAL = { isActive: false, stepIndex: 0 };

function reducer(state, action) {
  switch (action.type) {
    case "START":
      return { isActive: true, stepIndex: 0 };
    case "NEXT": {
      if (!state.isActive) return state;
      const next = state.stepIndex + 1;
      if (next >= FIGUTOUR_STEPS.length) return state; // Finish handled by caller
      return { ...state, stepIndex: next };
    }
    case "PREV": {
      if (!state.isActive) return state;
      const prev = state.stepIndex - 1;
      if (prev < 0) return state;
      return { ...state, stepIndex: prev };
    }
    case "END":
      return { isActive: false, stepIndex: 0 };
    default:
      return state;
  }
}

const FiguTourContext = createContext({
  isActive: false,
  stepIndex: 0,
  currentStep: null,
  totalSteps: FIGUTOUR_STEPS.length,
  startTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
  previousStep: () => {},
  endTour: () => {},
});

export function FiguTourProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const { isActive, stepIndex } = state;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { setState: setExpression } = useRoboBallExpression();

  const autoTriggerCheckedRef = useRef(false);
  const persistedRef = useRef(false);

  const currentStep = isActive ? FIGUTOUR_STEPS[stepIndex] || null : null;

  // Debug trace — user requested for verification traceability.
  useEffect(() => {
    if (isActive) {
      // eslint-disable-next-line no-console
      console.debug(
        "[figutour] step change",
        stepIndex + 1,
        currentStep?.id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, isActive]);

  const persistCompletion = useCallback(() => {
    if (persistedRef.current) return;
    persistedRef.current = true;
    api
      .post("/onboarding/figutour-completed")
      .catch(() => {
        // Silent — retry allowed on next call.
        persistedRef.current = false;
      });
  }, []);

  const startTour = useCallback(() => {
    // eslint-disable-next-line no-console
    console.debug("[figutour] startTour called");
    persistedRef.current = false;
    logEvent("figutour_started");
    setExpression("smile");
    dispatch({ type: "START" });
    // Ensure #inventory-grid target is reachable. Sidebar nav items are
    // visible from every /app/* route so no per-step navigation is needed.
    if (
      typeof window !== "undefined" &&
      window.location.pathname !== "/app"
    ) {
      navigate("/app");
    }
  }, [navigate, setExpression]);

  const endTour = useCallback(
    (opts) => {
      const completed = !!(opts && opts.completed);
      // eslint-disable-next-line no-console
      console.debug("[figutour] endTour called, completed=", completed);
      dispatch({ type: "END" });
      setExpression("idle");
      if (completed) {
        logEvent("figutour_completed");
      }
      persistCompletion();
    },
    [persistCompletion, setExpression]
  );

  const nextStep = useCallback(() => {
    // eslint-disable-next-line no-console
    console.debug("[figutour] nextStep clicked");
    // Peek at current step BEFORE dispatch so we can detect "Finish" state.
    // We read stepIndex via a ref-of-state pattern via reducer semantics —
    // if we're on the last step, dispatch END instead of NEXT.
  }, []);

  // Wrap nextStep so it can access latest state via closure — recreated
  // when stepIndex changes, which is what we want for the Finish detection.
  const nextStepStable = useCallback(() => {
    // eslint-disable-next-line no-console
    console.debug("[figutour] nextStep clicked, current=", stepIndex + 1);
    if (stepIndex >= FIGUTOUR_STEPS.length - 1) {
      endTour({ completed: true });
      return;
    }
    dispatch({ type: "NEXT" });
  }, [stepIndex, endTour]);

  const prevStep = useCallback(() => {
    // eslint-disable-next-line no-console
    console.debug("[figutour] prevStep clicked, current=", stepIndex + 1);
    dispatch({ type: "PREV" });
  }, [stepIndex]);

  const endTourNoComplete = useCallback(() => {
    endTour({ completed: false });
  }, [endTour]);

  // Auto-trigger on first authenticated /app visit (once per tab session).
  useEffect(() => {
    if (autoTriggerCheckedRef.current) return;
    if (!user) return;
    if (!location.pathname.startsWith("/app")) return;
    autoTriggerCheckedRef.current = true;

    api
      .get("/onboarding/figutour-status")
      .then((res) => {
        // eslint-disable-next-line no-console
        console.debug(
          "[figutour] status fetched:",
          res.data
        );
        if (res.data?.has_seen_figutour === false) {
          setTimeout(() => {
            logEvent("figutour_autotriggered");
            startTour();
          }, 600);
        }
      })
      .catch(() => {
        /* silent */
      });
  }, [user, location.pathname, startTour]);

  const value = useMemo(
    () => ({
      isActive,
      stepIndex,
      currentStep,
      totalSteps: FIGUTOUR_STEPS.length,
      startTour,
      nextStep: nextStepStable,
      prevStep,
      previousStep: prevStep, // backwards-compat alias
      // endTour accepts `{completed?}` — Finish path passes true, other
      // exits (skip / esc / backdrop / back-past-step-1) omit it.
      endTour,
      endTourSkip: endTourNoComplete,
    }),
    [
      isActive,
      stepIndex,
      currentStep,
      startTour,
      nextStepStable,
      prevStep,
      endTour,
      endTourNoComplete,
    ]
  );

  // Suppress the unused-var warning for the placeholder nextStep — it's
  // intentionally kept for symmetry with early revs; the stable version is
  // what's exposed. Referencing it via void keeps ESLint happy.
  void nextStep;

  return (
    <FiguTourContext.Provider value={value}>
      {children}
      {isActive && currentStep ? (
        <>
          <FiguTourOverlay />
          <FiguTourBubble />
        </>
      ) : null}
    </FiguTourContext.Provider>
  );
}

export function useFiguTour() {
  return useContext(FiguTourContext);
}
